import type { WidgetTab } from "@/context/RightNavContext";
import { useCallback, useMemo } from "react";
import { Action, TrackObject, useAnalyticsTracker } from "./AnalyticsProvider";

export type WidgetTrackingEvent = {
  action: Action.OPENED;
  extras: {
    id: string;
    type: "button" | "link" | "card" | "tab";
    label: string;
    section: string;
    targetTab?: WidgetTab;
  } & Record<string, number | string | boolean>;
};

/**
 * Hook to track widget-related analytics events
 *
 * @example
 * ```tsx
 * const { trackWidgetOpen } = useWidgetAnalytics();
 *
 * // Basic usage
 * trackWidgetOpen({
 *   widgetTab: "Swap",
 *   id: "fund-button",
 *   type: "button",
 *   label: "Fund",
 *   section: "RightNav",
 * });
 *
 * // With extra context
 * trackWidgetOpen({
 *   widgetTab: "Buy",
 *   id: "quest-buy-button",
 *   type: "button",
 *   label: "Buy tokens for quest",
 *   section: "QuestDetails",
 *   extras: { questId: "123", questName: "Daily Challenge" },
 * });
 * ```
 */
export const useWidgetAnalytics = () => {
  const { track } = useAnalyticsTracker<WidgetTrackingEvent>(
    TrackObject.WIDGET,
  );

  const trackWidgetOpen = useCallback(
    (params: {
      widgetTab: WidgetTab;
      id: string;
      type: "button" | "link" | "card" | "tab";
      label: string;
      section: string;
      extras?: Record<string, number | string | boolean>;
    }) => {
      track({
        action: Action.OPENED,
        extras: {
          id: params.id,
          type: params.type,
          label: params.label,
          section: params.section,
          targetTab: params.widgetTab,
          ...params.extras,
        },
      });
    },
    [track],
  );

  return useMemo(() => ({ track, trackWidgetOpen }), [track, trackWidgetOpen]);
};
